import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  GitBranch,
  Plus,
  ChevronDown,
  ChevronUp,
  Users,
  AlertTriangle,
  CheckCircle,
  Clock,
  Trash2,
  RefreshCw,
  BarChart3,
  X,
} from "lucide-react";
import { apiClient } from "../services/api";

// ─── helpers ────────────────────────────────────────────────────────────────

const CRITICALITY_COLORS = {
  critical: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  high: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  medium: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  low: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
};

const READINESS_COLORS = {
  ready_now: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  ready_1_2_years: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  ready_3_5_years: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  long_term: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};

const criticalityLabel = (value, t) => t(`successionHub.criticality.${value}`, { defaultValue: value });
const readinessLabel = (value, t) => t(`successionHub.readiness.${value}`, { defaultValue: value });
const riskLabel = (value, t) => t(`successionHub.risk.${value}`, { defaultValue: value });

const Badge = ({ label, colorClass }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>{label}</span>
);

const KpiCard = ({ title, value, sub, icon: Icon, color = "blue" }) => {
  const colors = {
    blue: "text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400",
    red: "text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400",
    orange: "text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400",
    green: "text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400",
    purple: "text-purple-600 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400",
  };
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-3xl font-bold">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-lg ${colors[color]}`}>
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
};

// ─── Tab: Tableau de bord ────────────────────────────────────────────────────

const DashboardTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("v1/succession/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title={t("successionHub.dashboard.kpiActivePlans")} value={data.total_plans} icon={GitBranch} color="blue" />
        <KpiCard title={t("successionHub.dashboard.kpiCriticalPlans")} value={data.critical_plans} icon={AlertTriangle} color="red" />
        <KpiCard title={t("successionHub.dashboard.kpiNoSuccessor")} value={data.plans_with_no_successor} sub={t("successionHub.dashboard.uncoveredPositions")} icon={Users} color="orange" />
        <KpiCard title={t("successionHub.dashboard.kpiBenchStrength")} value={`${data.bench_strength_pct}%`} sub={t("successionHub.dashboard.readySuccessorPlans")} icon={CheckCircle} color="green" />
      </div>

      {/* Readiness distribution */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><Clock size={16} /> {t("successionHub.dashboard.readinessDistribution")}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(data.readiness_distribution).map(([key, { count, label }]) => (
            <div key={key} className="text-center p-3 rounded-lg bg-muted/30">
              <p className="text-2xl font-bold">{count}</p>
              <p className="text-xs text-muted-foreground mt-1">{readinessLabel(key, t) || label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Department coverage */}
      {data.department_coverage?.length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="font-semibold flex items-center gap-2"><BarChart3 size={16} /> {t("successionHub.dashboard.departmentCoverage")}</h3>
          </div>
          <div className="divide-y divide-border">
            {data.department_coverage.map((dept) => (
              <div key={dept.department} className="px-5 py-3 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{dept.department}</p>
                  <p className="text-xs text-muted-foreground">{t("successionHub.dashboard.coveredPositions", { covered: dept.covered, total: dept.total })}</p>
                </div>
                <div className="w-32">
                  <div className="h-2 rounded-full bg-muted">
                    <div className={`h-2 rounded-full ${dept.coverage_pct >= 80 ? "bg-green-500" : dept.coverage_pct >= 50 ? "bg-orange-400" : "bg-red-500"}`}
                      style={{ width: `${dept.coverage_pct}%` }} />
                  </div>
                </div>
                <span className="text-sm font-semibold w-12 text-right">{dept.coverage_pct}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Plans ───────────────────────────────────────────────────────────────

const CreatePlanModal = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ position_title: "", criticality: "medium", vacancy_reason: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.position_title.trim()) return;
    setSaving(true);
    try {
      await apiClient.post("v1/succession/plans", form);
      onCreated();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold text-lg">{t("successionHub.planModal.title")}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t("successionHub.planModal.fieldPosition")}</label>
            <input className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              value={form.position_title} onChange={(e) => setForm({ ...form, position_title: e.target.value })} placeholder={t("successionHub.planModal.positionPlaceholder")} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("successionHub.planModal.fieldCriticality")}</label>
            <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              value={form.criticality} onChange={(e) => setForm({ ...form, criticality: e.target.value })}>
              {["critical", "high", "medium", "low"].map((k) => <option key={k} value={k}>{criticalityLabel(k, t)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("successionHub.planModal.fieldVacancyReason")}</label>
            <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              value={form.vacancy_reason} onChange={(e) => setForm({ ...form, vacancy_reason: e.target.value })}>
              <option value="">{t("successionHub.common.select")}</option>
              <option value="retirement">{t("successionHub.vacancyReason.retirement")}</option>
              <option value="promotion">{t("successionHub.vacancyReason.promotion")}</option>
              <option value="resignation">{t("successionHub.vacancyReason.resignation")}</option>
              <option value="growth">{t("successionHub.vacancyReason.growth")}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("successionHub.planModal.fieldNotes")}</label>
            <textarea className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm" rows={2}
              value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-muted">{t("successionHub.common.cancel")}</button>
          <button onClick={save} disabled={saving || !form.position_title.trim()}
            className="px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground disabled:opacity-50">
            {saving ? t("successionHub.planModal.creating") : t("successionHub.planModal.createCta")}
          </button>
        </div>
      </div>
    </div>
  );
};

const AddSuccessorModal = ({ planId, onClose, onAdded }) => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_id: "", readiness: "ready_1_2_years", rank: 1, retention_risk: "low" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("v1/employees/?limit=200").then((r) => setEmployees(r.data.employees || r.data.items || []));
  }, []);

  const save = async () => {
    if (!form.employee_id) return;
    setSaving(true);
    try {
      await apiClient.post(`v1/succession/plans/${planId}/successors`, { ...form, employee_id: parseInt(form.employee_id) });
      onAdded();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold text-lg">{t("successionHub.successorModal.title")}</h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t("successionHub.successorModal.fieldEmployee")}</label>
            <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
              <option value="">{t("successionHub.common.select")}</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t("successionHub.successorModal.fieldReadiness")}</label>
            <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
              value={form.readiness} onChange={(e) => setForm({ ...form, readiness: e.target.value })}>
              {["ready_now", "ready_1_2_years", "ready_3_5_years", "long_term"].map((k) => <option key={k} value={k}>{readinessLabel(k, t)}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">{t("successionHub.successorModal.fieldRank")}</label>
              <input type="number" min={1} className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                value={form.rank} onChange={(e) => setForm({ ...form, rank: parseInt(e.target.value) || 1 })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t("successionHub.successorModal.fieldRetentionRisk")}</label>
              <select className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
                value={form.retention_risk} onChange={(e) => setForm({ ...form, retention_risk: e.target.value })}>
                <option value="low">{riskLabel("low", t)}</option>
                <option value="medium">{riskLabel("medium", t)}</option>
                <option value="high">{riskLabel("high", t)}</option>
              </select>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-border hover:bg-muted">{t("successionHub.common.cancel")}</button>
          <button onClick={save} disabled={saving || !form.employee_id}
            className="px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground disabled:opacity-50">
            {saving ? t("successionHub.successorModal.adding") : t("successionHub.successorModal.addCta")}
          </button>
        </div>
      </div>
    </div>
  );
};

const PlanCard = ({ plan, onRefresh }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [showAddSuccessor, setShowAddSuccessor] = useState(false);

  const removeSuccessor = async (successorId) => {
    if (!window.confirm(t("successionHub.plans.confirmRemoveSuccessor"))) return;
    await apiClient.delete(`v1/succession/successors/${successorId}`);
    onRefresh();
  };

  const deletePlan = async () => {
    if (!window.confirm(t("successionHub.plans.confirmDeletePlan"))) return;
    await apiClient.delete(`v1/succession/plans/${plan.id}`);
    onRefresh();
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-5 py-4 flex items-start justify-between gap-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold">{plan.position_title}</p>
            <Badge label={criticalityLabel(plan.criticality, t)}
              colorClass={CRITICALITY_COLORS[plan.criticality] || ""} />
            {!plan.has_ready_now && plan.successor_count === 0 && (
              <Badge label={t("successionHub.plans.uncovered")} colorClass="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" />
            )}
            {plan.has_ready_now && (
              <Badge label={t("successionHub.plans.covered")} colorClass="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" />
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
            {plan.department_name && <span>{plan.department_name}</span>}
            {plan.incumbent_name && <span>{t("successionHub.plans.incumbent", { name: plan.incumbent_name })}</span>}
            {plan.expected_vacancy_date && <span>{t("successionHub.plans.expectedVacancy", { date: plan.expected_vacancy_date })}</span>}
            <span>{t("successionHub.plans.successorCount", { count: plan.successor_count })}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={(e) => { e.stopPropagation(); deletePlan(); }} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-muted-foreground hover:text-red-500">
            <Trash2 size={14} />
          </button>
          {expanded ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border">
          {plan.successors?.length > 0 ? (
            <div className="divide-y divide-border">
              {plan.successors.map((s) => (
                <div key={s.id} className="px-5 py-3 flex items-center gap-4">
                  <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">{s.rank}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{s.employee_name}</p>
                    <p className="text-xs text-muted-foreground">{s.department} · {s.job_title || "—"}</p>
                  </div>
                  <Badge label={readinessLabel(s.readiness, t)}
                    colorClass={READINESS_COLORS[s.readiness] || ""} />
                  {s.retention_risk === "high" && (
                    <Badge label={t("successionHub.plans.departureRisk")} colorClass="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" />
                  )}
                  <button onClick={() => removeSuccessor(s.id)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-muted-foreground hover:text-red-500">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-5 py-4 text-sm text-muted-foreground italic">{t("successionHub.plans.noSuccessor")}</div>
          )}
          <div className="px-5 py-3 border-t border-border">
            <button onClick={() => setShowAddSuccessor(true)}
              className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80">
              <Plus size={14} /> {t("successionHub.plans.addSuccessor")}
            </button>
          </div>
        </div>
      )}

      {showAddSuccessor && (
        <AddSuccessorModal planId={plan.id} onClose={() => setShowAddSuccessor(false)} onAdded={onRefresh} />
      )}
    </div>
  );
};

const PlansTab = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get("v1/succession/plans").then((r) => setPlans(r.data.items || [])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t("successionHub.plans.count", { count: plans.length })}</p>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <RefreshCw size={14} />
          </button>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
            <Plus size={15} /> {t("successionHub.plans.new")}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
      ) : plans.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-xl">
          <GitBranch size={40} className="mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">{t("successionHub.plans.empty")}</p>
          <p className="text-sm text-muted-foreground mt-1">{t("successionHub.plans.emptyHint")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {plans.map((plan) => <PlanCard key={plan.id} plan={plan} onRefresh={load} />)}
        </div>
      )}

      {showCreate && <CreatePlanModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
};

// ─── Tab: Cartographie ───────────────────────────────────────────────────────

const MapTab = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("v1/succession/plans").then((r) => setPlans(r.data.items || [])).finally(() => setLoading(false));
  }, []);

  const criticalityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  const sorted = [...plans].sort((a, b) => (criticalityOrder[a.criticality] || 3) - (criticalityOrder[b.criticality] || 3));

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="font-semibold">{t("successionHub.map.title")}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{t("successionHub.map.subtitle")}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("successionHub.map.colPosition")}</th>
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("successionHub.map.colDepartment")}</th>
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("successionHub.map.colCriticality")}</th>
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("successionHub.map.colIncumbent")}</th>
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("successionHub.map.colTopSuccessor")}</th>
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">{t("successionHub.map.colReadiness")}</th>
                <th className="text-center px-5 py-3 font-medium text-muted-foreground">{t("successionHub.map.colCoverage")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sorted.map((plan) => {
                const top = plan.successors?.[0];
                return (
                  <tr key={plan.id} className="hover:bg-muted/20">
                    <td className="px-5 py-3 font-medium">{plan.position_title}</td>
                    <td className="px-5 py-3 text-muted-foreground">{plan.department_name || "—"}</td>
                    <td className="px-5 py-3">
                      <Badge label={criticalityLabel(plan.criticality, t)} colorClass={CRITICALITY_COLORS[plan.criticality]} />
                    </td>
                    <td className="px-5 py-3 text-muted-foreground">{plan.incumbent_name || "—"}</td>
                    <td className="px-5 py-3 font-medium">{top?.employee_name || <span className="italic text-muted-foreground">{t("successionHub.map.notIdentified")}</span>}</td>
                    <td className="px-5 py-3">
                      {top ? <Badge label={readinessLabel(top.readiness, t)} colorClass={READINESS_COLORS[top.readiness]} /> : "—"}
                    </td>
                    <td className="px-5 py-3 text-center">
                      {plan.has_ready_now ? (
                        <CheckCircle size={16} className="inline text-green-500" />
                      ) : plan.successor_count > 0 ? (
                        <Clock size={16} className="inline text-yellow-500" />
                      ) : (
                        <AlertTriangle size={16} className="inline text-red-500" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Hub ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: "dashboard", icon: BarChart3 },
  { id: "plans", icon: GitBranch },
  { id: "map", icon: Users },
];

export default function SuccessionHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "dashboard";

  const setTab = (id) => setSearchParams({ tab: id });

  const renderContent = () => {
    switch (activeTab) {
      case "plans": return <PlansTab />;
      case "map": return <MapTab />;
      default: return <DashboardTab />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <GitBranch size={24} className="text-primary" />
            {t("successionHub.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("successionHub.subtitle")}
          </p>
        </div>
      </div>

      <div className="flex gap-1 bg-muted/50 rounded-xl p-1 w-fit">
        {TABS.map(({ id, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === id ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <Icon size={15} />
            {t(`successionHub.tabs.${id}`)}
          </button>
        ))}
      </div>

      {renderContent()}
    </div>
  );
}
